'use client';

import { useId } from 'react';
import { cn } from '@/components/ui';
import { LOGO_PALETTE } from '@/lib/brand/logoPalette';

export type TechnoConixLogoVariant = 'light' | 'dark';

export type TechnoConixLogoProps = {
  variant?: TechnoConixLogoVariant;
  className?: string;
  'aria-label'?: string;
  title?: string;
};

const WORD_FONT =
  "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const LOGO_VIEW = { width: 258, height: 44 } as const;
const LOGO_TEXT = { x: 50, y: 22, size: 33, tracking: '-0.035em' } as const;

const HUB = { x: 22, y: 22 };
const NODES = {
  top: { x: 22, y: 7.75 },
  br: { x: 34.5, y: 31.5 },
  bl: { x: 9.5, y: 31.5 },
} as const;

const MESH_PATH = `M${NODES.top.x} ${NODES.top.y} L${NODES.br.x} ${NODES.br.y} L${NODES.bl.x} ${NODES.bl.y} Z`;
const LINK_PATHS = {
  top: `M${HUB.x} ${HUB.y} L${NODES.top.x} ${NODES.top.y}`,
  br: `M${HUB.x} ${HUB.y} L${NODES.br.x} ${NODES.br.y}`,
  bl: `M${HUB.x} ${HUB.y} L${NODES.bl.x} ${NODES.bl.y}`,
} as const;

export function TechnoConixLogo({
  variant = 'light',
  className,
  'aria-label': ariaLabel = 'TechnoConix',
  title,
}: TechnoConixLogoProps) {
  const uid = useId().replace(/:/g, '');
  const markGrad = `tc-brand-mark-${uid}`;
  const wordGrad = `tc-brand-word-${uid}`;
  const palette = LOGO_PALETTE[variant];

  const nodeSpecs = [
    { key: 'top', ...NODES.top, fill: palette.nodes.top, className: 'tc-logo-mark__node--top' },
    { key: 'br', ...NODES.br, fill: palette.nodes.br, className: 'tc-logo-mark__node--br' },
    { key: 'bl', ...NODES.bl, fill: palette.nodes.bl, className: 'tc-logo-mark__node--bl' },
  ] as const;

  const linkSpecs = [
    { key: 'top', d: LINK_PATHS.top, className: 'tc-logo-mark__link--top' },
    { key: 'br', d: LINK_PATHS.br, className: 'tc-logo-mark__link--br' },
    { key: 'bl', d: LINK_PATHS.bl, className: 'tc-logo-mark__link--bl' },
  ] as const;

  return (
    <svg
      className={cn('tc-logo', className)}
      width={LOGO_VIEW.width}
      height={LOGO_VIEW.height}
      viewBox={`0 0 ${LOGO_VIEW.width} ${LOGO_VIEW.height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
    >
      {title ? <title>{title}</title> : null}
      <defs>
        <linearGradient id={markGrad} x1="4" y1="40" x2="40" y2="4" gradientUnits="userSpaceOnUse">
          <stop stopColor={palette.markGradient.from} />
          <stop offset="1" stopColor={palette.markGradient.to} />
        </linearGradient>
        <linearGradient id={wordGrad} x1="0" y1="0" x2="1" y2="0" gradientUnits="objectBoundingBox">
          <stop stopColor={palette.wordGradient.from} />
          <stop offset="1" stopColor={palette.wordGradient.to} />
        </linearGradient>
      </defs>
      <g className="tc-logo-mark" aria-hidden="true">
        <rect className="tc-logo-mark__bg" x="1" y="1" width="42" height="42" rx="11" fill={palette.tileFill} />
        <rect
          className="tc-logo-mark__frame"
          x="1"
          y="1"
          width="42"
          height="42"
          rx="11"
          fill="none"
          stroke={`url(#${markGrad})`}
          strokeOpacity={palette.frameOpacity}
          strokeWidth="1.5"
          pathLength="1"
        />
        <g className="tc-logo-mark__network">
          <path
            className="tc-logo-mark__mesh"
            d={MESH_PATH}
            stroke={`url(#${markGrad})`}
            strokeOpacity={palette.meshOpacity}
            strokeWidth="1.75"
            strokeLinejoin="round"
            pathLength="1"
          />
          {linkSpecs.map(({ key, d, className: linkClass }) => (
            <g key={key} className={cn('tc-logo-mark__link-group', linkClass)}>
              <path
                className="tc-logo-mark__link tc-logo-mark__link--base"
                d={d}
                stroke={`url(#${markGrad})`}
                strokeOpacity={palette.linkOpacity}
                strokeWidth="3.25"
                strokeLinecap="round"
                pathLength="1"
              />
              <path
                className="tc-logo-mark__link tc-logo-mark__link--signal"
                d={d}
                stroke={`url(#${markGrad})`}
                strokeWidth="2"
                strokeLinecap="round"
                pathLength="1"
              />
            </g>
          ))}
        </g>
        <circle
          className="tc-logo-mark__hub-ring"
          cx={HUB.x}
          cy={HUB.y}
          r="6.25"
          stroke={`url(#${markGrad})`}
          strokeOpacity={palette.hubRingOpacity}
          strokeWidth="1.35"
          fill="none"
          pathLength="1"
        />
        <circle className="tc-logo-mark__hub" cx={HUB.x} cy={HUB.y} r="4.35" fill={`url(#${markGrad})`} />
        {nodeSpecs.map(({ key, x, y, fill, className: nodeClass }) => (
          <g key={key} className={cn('tc-logo-mark__node', nodeClass)}>
            <circle
              className="tc-logo-mark__node-ring"
              cx={x}
              cy={y}
              r="3.35"
              stroke={`url(#${markGrad})`}
              strokeOpacity={palette.hubRingOpacity}
              strokeWidth="1.35"
              fill="none"
            />
            <circle className="tc-logo-mark__node-core" cx={x} cy={y} r="2" fill={fill} />
          </g>
        ))}
      </g>
      <text
        x={LOGO_TEXT.x}
        y={LOGO_TEXT.y}
        dominantBaseline="central"
        fontFamily={WORD_FONT}
        fontSize={LOGO_TEXT.size}
        fontWeight="700"
        letterSpacing={LOGO_TEXT.tracking}
        fill={palette.ink}
      >
        <tspan>Techno</tspan>
        <tspan fill={`url(#${wordGrad})`}>Conix</tspan>
      </text>
    </svg>
  );
}
